import React, { useEffect, useState, useCallback } from 'react';
import { FiBell } from 'react-icons/fi';
import { useNotifications } from '../../hooks/useNotifications';

const STORAGE_KEY = 'shipmentCocaptain_notifications_muted';

const NotificationSettings: React.FC = () => {
  const { notificationState, requestPermission } = useNotifications();

  // persisted muted state - when true we suppress sounds/notifications locally
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Keep localStorage in sync
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, muted ? 'true' : 'false');
    } catch (e) {
      // ignore
    }
  }, [muted]);

  // If notifications become denied (external change), clear muted
  useEffect(() => {
    if (notificationState.permission === 'denied' && muted) {
      setMuted(false);
    }
  }, [notificationState.permission, muted]);

  const handleClick = useCallback(async () => {
    // If permission not yet granted, request it
    if (notificationState.permission !== 'granted') {
      const permission = await requestPermission();
      if (permission === 'granted') {
        setMuted(false);
      }
      return;
    }

    // If already granted, toggle local mute state
    setMuted(prev => !prev);
  }, [notificationState.permission, requestPermission]);

  // Tooltip text
  const getTooltip = () => {
    if (!notificationState.supported) return 'Notifications not supported';
    if (notificationState.permission === 'default') return 'Enable notifications';
    if (notificationState.permission === 'denied') return 'Notifications blocked in browser';
    return muted ? 'Notifications silent (click to unmute)' : 'Notifications enabled (click to mute)';
  };

  // If notifications are not supported, render nothing
  if (!notificationState.supported) return null;

  // Visual state classes (theme-friendly)
  const base = 'p-2 rounded-full transition-all duration-200 flex items-center justify-center';
  const neutralBg = 'bg-neutral-light dark:bg-dark-accent';

  // Text color: active uses primary, otherwise tertiary text
  const textColor = notificationState.permission === 'granted' && !muted
    ? 'text-primary'
    : 'text-text-tertiary dark:text-dark-textSecondary';

  // Active glow when notifications enabled and not muted
  const activeGlow = notificationState.permission === 'granted' && !muted
    ? 'glow-md'
    : '';

  // Slash opacity - darker when muted
  const slashOpacity = muted ? 'opacity-90' : 'opacity-0';

  return (
    <button
      onClick={handleClick}
      title={getTooltip()}
      aria-label={getTooltip()}
      className={`${base} ${neutralBg} ${textColor} ${activeGlow} hover-glow-sm`}
    >
      <div className="relative w-5 h-5 flex items-center justify-center">
        <FiBell className="w-5 h-5" />
        {/* diagonal slash - uses current color (text color) */}
        <span
          className={`absolute transform rotate-45 w-5 h-0.5 bg-current ${slashOpacity} pointer-events-none`}
          style={{ top: '50%', left: 0 }}
        />
      </div>
    </button>
  );
};

export default NotificationSettings;
